import {
  faChevronDown,
  faLocation,
  faTimesCircle,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { isEmpty, reverse } from 'ramda'
import React, { useState, Children, useEffect, forwardRef } from 'react'
import { Selection, SelectVariant } from '../Select'
import { twMerge } from 'tailwind-merge'
import { ControllerFieldState } from 'react-hook-form'
import { Button } from '../../../Button'
import { InputIcon } from '../../Input/InputIcon'

type Props = {
  multiple?: boolean
  placeholder?: string
  noLabel?: boolean
  label: string
  optionsVisible: boolean
  setOptionsVisible: () => void
  selections: Selection<any>[]
  ['data-testid']?: string
  handleSelect: (selection: Selection<any>) => void
  children?: React.ReactNode
  className?: string
  disabled?: boolean
  icon?: IconDefinition
  fieldState?: ControllerFieldState
  stopPropagation?: boolean
  variant: SelectVariant
}

export const SelectOptionButton = forwardRef<HTMLButtonElement, Props>(
  (props, ref) => {
    const prefixedDropdownClasses = twMerge(
      'rounded-md flex space-x-2 items-center justify-center relative px-4 py-2 text-blue-500 text-sm font-semibold transition-all duration-300 ease-in-out group disabled:border-none disabled:bg-base-300 disabled:text-base-600 disabled:hover:text-base-600 disabled:hover:cursor-not-allowed',
      'text-indigo-600 hover:text-indigo-800 dark:text-pink-400 dark:hover:text-pink-600 px-0 text-md p-0',
    )

    if (props.variant === 'switch-group') return null
    if (props.variant === 'pill-group') return null

    if (props.variant === 'prefixed-dropdown')
      return (
        <div className='flex space-x-2'>
          {props.selections && props.selections.length > 0 && (
            <p
              className='font-semibold text-base/7 text-base-900  dark:text-white text-center'
              onClick={(e) => {
                props.setOptionsVisible()
                if (props.stopPropagation) e.stopPropagation()
              }}>
              {!props.selections || !props.placeholder
                ? props.label
                : props.label}
            </p>
          )}

          <Button
            ref={ref}
            variant='unstyled'
            className={prefixedDropdownClasses}
            icon={faChevronDown}
            disabled={props.disabled}
            iconPosition='right'
            onClick={(e) => {
              props.setOptionsVisible()
              if (props.stopPropagation) e.stopPropagation()
            }}>
            <span className='flex-1' data-testid='current-option'>
              {/* Placeholder */}
              {isEmpty(props.selections) &&
                (props.placeholder || props.label) && (
                  <p className=''>{props.placeholder || props.label}</p>
                )}

              {/* Single Select */}
              {!props.multiple && props.selections[0]?.label}

              {/* Multiple Select */}
              {props.multiple && (
                <div className='w-full h-full flex gap-1'>
                  {/* Show Most Recent Selections First */}
                  {props.selections
                    .slice(0, props.selections.length > 4 ? 3 : 4)
                    .map((selection) => (
                      <div
                        key={
                          typeof selection.value === 'object'
                            ? JSON.stringify(selection.value)
                            : selection.value
                        }
                        className='flex bg-blue-100 border text-blue-900 text-xs font-medium px-2 py-0.5 items-center justify-center rounded-full max-w-min'>
                        <p className='truncate'>{selection.label}</p>
                        <span
                          onClick={(e) => {
                            e.stopPropagation()
                            props.handleSelect(selection)
                          }}>
                          <FontAwesomeIcon
                            size='sm'
                            className={`text-red-500 ml-2 rounded-full`}
                            icon={faTimesCircle}
                          />
                        </span>
                      </div>
                    ))}
                  {props.selections.length > 4 && (
                    <div
                      key={'...'}
                      className='flex bg-blue-100 border text-blue-900 text-xs font-medium px-2 py-0.5 items-center justify-center rounded-full max-w-min'>
                      <p>...</p>
                    </div>
                  )}
                </div>
              )}
            </span>
          </Button>
        </div>
      )

    return (
      <button
        ref={ref}
        className={twMerge(
          'focus:outline-none max-w-full min-w-0 shadow-sm focus:shadow-outline-blue focus:border-blue-300 dark:focus:border-gray-500 sm:text-sm sm:leading-5 block w-full px-3 pr-1 py-2 text-left placeholder-gray-400 truncate transition duration-150 ease-in-out bg-white border border-gray-300  dark:border-white/5 rounded-md appearance-none cursor-pointer overflow-y-auto sm:overflow-hidden disabled:bg-base-200 disabled:cursor-not-allowed dark:bg-white/10 dark:text-white',
          props.fieldState?.invalid &&
            'border-red-400 focus-within:shadow-outline-blue focus:border-red-400 dark:focus:border-red-800',
          props.className,
        )}
        disabled={props.disabled}
        type='button'
        aria-haspopup='listbox'
        aria-expanded={props.optionsVisible}
        data-testid={props['data-testid'] || 'select-button'}
        onClick={(e) => {
          props.setOptionsVisible()
          if (props.stopPropagation) e.stopPropagation()
        }}>
        <div
          className={`flex items-center space-x-2 ${
            props.optionsVisible ? 'overflow-y-auto' : 'overflow-hidden'
          }`}>
          <InputIcon
            invalid={props.fieldState?.invalid}
            icon={props.icon}
            className='px-0'
          />

          <span className='flex-1' data-testid='current-option'>
            {/* Placeholder */}
            {isEmpty(props.selections) &&
              (props.placeholder || props.label) && (
                <p className='text-base-500'>
                  {props.placeholder || props.label}
                </p>
              )}

            {/* Single Select */}
            {!props.multiple && props.selections[0]?.label}

            {/* Multiple Select */}
            {props.multiple && (
              <div className='w-full h-full flex gap-1'>
                {/* Show Most Recent Selections First */}
                {props.selections
                  .slice(0, props.selections.length > 4 ? 3 : 4)
                  .map((selection) => (
                    <div
                      key={
                        typeof selection.value === 'object'
                          ? JSON.stringify(selection.value)
                          : selection.value
                      }
                      className='flex bg-blue-100 border text-blue-900 text-xs font-medium px-2 py-0.5 items-center justify-center rounded-full max-w-min'>
                      <p className='truncate'>{selection.label}</p>
                      <span
                        onClick={(e) => {
                          e.stopPropagation()
                          props.handleSelect(selection)
                        }}>
                        <FontAwesomeIcon
                          size='sm'
                          className={`text-red-500 ml-2 rounded-full`}
                          icon={faTimesCircle}
                        />
                      </span>
                    </div>
                  ))}
                {props.selections.length > 4 && (
                  <div
                    key={'...'}
                    className='flex bg-blue-100 border text-blue-900 text-xs font-medium px-2 py-0.5 items-center justify-center rounded-full max-w-min'>
                    <p>...</p>
                  </div>
                )}
              </div>
            )}
          </span>

          {props.variant === 'form-field' && (
            <svg
              className='w-5 h-5 text-base-400'
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 20 20'
              fill='currentColor'
              aria-hidden='true'>
              <path
                fillRule='evenodd'
                d='M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z'
                clipRule='evenodd'
              />
            </svg>
          )}
        </div>
      </button>
    )
  },
)
